import React, { useEffect, useState } from "react";
import { getStorageInfo } from "../../api/settingsApi";
import { AUTH_STORAGE_KEY } from "../../socket";
import "../../styles.css";

function StorageSettings() {
  const [storageInfo, setStorageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStorageInfo();
  }, []);

  const fetchStorageInfo = async () => {
    try {
      setLoading(true);
      setError("");
      const rawAuth = localStorage.getItem(AUTH_STORAGE_KEY);
      const parsedAuth = rawAuth ? JSON.parse(rawAuth) : null;
      const authHeaders = parsedAuth?.token ? { Authorization: `Bearer ${parsedAuth.token}` } : null;
      
      if (!authHeaders) {
        throw new Error("Not authenticated");
      }
      
      const data = await getStorageInfo(authHeaders);
      setStorageInfo(data);
    } catch (err) {
      setError(err.message || "Failed to fetch storage info");
      console.error("Storage fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const getStorageStatus = (percentUsed) => {
    if (percentUsed < 50) return "normal";
    if (percentUsed < 80) return "warning";
    return "critical";
  };

  if (loading) {
    return (
      <div className="storage-settings">
        <h3>Storage Usage</h3>
        <p>Loading storage information...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="storage-settings">
        <h3>Storage Usage</h3>
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!storageInfo) {
    return (
      <div className="storage-settings">
        <h3>Storage Usage</h3>
        <p>Unable to load storage information</p>
      </div>
    );
  }

  const status = getStorageStatus(storageInfo.percentUsed);

  return (
    <div className="storage-settings">
      <h3>Storage Usage</h3>

      <div className="storage-container">
        <div className="storage-info-grid">
          <div className="storage-info-item">
            <span className="storage-label">Total Storage</span>
            <span className="storage-value">{storageInfo.totalStorageGB} GB</span>
          </div>
          <div className="storage-info-item">
            <span className="storage-label">Used Storage</span>
            <span className="storage-value">{storageInfo.usedStorageGB} GB</span>
          </div>
          <div className="storage-info-item">
            <span className="storage-label">Available Storage</span>
            <span className="storage-value">{storageInfo.availableStorageGB} GB</span>
          </div>
          <div className="storage-info-item">
            <span className="storage-label">Usage</span>
            <span className="storage-value">{storageInfo.percentUsed}%</span>
          </div>
        </div>

        <div className="storage-progress-container">
          <div className={`storage-progress-bar storage-${status}`}>
            <div
              className="storage-progress-fill"
              style={{ width: `${storageInfo.percentUsed}%` }}
            ></div>
          </div>
          <p className="storage-progress-text">
            {storageInfo.usedStorageGB} GB of {storageInfo.totalStorageGB} GB used
          </p>
        </div>

        {status === "warning" && (
          <div className="storage-warning">
            <strong>⚠️ Warning:</strong> You are using over 80% of your storage. Consider deleting old emails or attachments.
          </div>
        )}

        {status === "critical" && (
          <div className="storage-critical">
            <strong>🔴 Critical:</strong> You are running out of storage space. Please delete some emails or attachments to free up space.
          </div>
        )}

        <button
          className="storage-refresh-btn"
          onClick={fetchStorageInfo}
          disabled={loading}
        >
          Refresh Storage Info
        </button>
      </div>
    </div>
  );
}

export default StorageSettings;
